import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { it } from "date-fns/locale";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, MapPin, Video, Trash2, Clock, Users, UserPlus, X, Send, Search, CalendarDays, Sparkles } from "lucide-react";

const MODALITA = {
  aula: { label: "Aula", chip: "bg-dusty-100 text-dusty-800 border-dusty-300", dot: "bg-dusty-500" },
  videoconferenza: { label: "Videoconferenza", chip: "bg-violet-100 text-violet-800 border-violet-300", dot: "bg-violet-500" },
};
const EMPTY_CORSISTA = { nome: "", cognome: "", cellulare: "", email: "" };
const fmtKey = (d) => format(d, "yyyy-MM-dd");
const fmtDayShort = (iso) => { try { return format(parseISO(iso), "d MMM", { locale: it }); } catch { return iso; } };
const fmtDayNum = (iso) => { try { return format(parseISO(iso), "d"); } catch { return iso; } };
const fmtMonShort = (iso) => { try { return format(parseISO(iso), "MMM", { locale: it }); } catch { return ""; } };
const fmtDayFull = (iso) => { try { return format(parseISO(iso), "EEEE d MMMM yyyy", { locale: it }); } catch { return iso; } };
const emptyEvent = () => ({ servizio_id: "", titolo: "", ore_totali: "", posti: "", giornate: [{ data: fmtKey(new Date()), ora_inizio: "09:00", ora_fine: "13:00", ore: "" }], modalita: "aula", luogo: "", link: "", note: "" });

export default function Calendar() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sendDlg, setSendDlg] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'new'|'edit'|'view', form, id}
  const [courseQuery, setCourseQuery] = useState("");
  const [modFilter, setModFilter] = useState("all");

  const range = useMemo(() => {
    if (view === "month") return { start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) };
    if (view === "week") return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    return { start: cursor, end: cursor };
  }, [view, cursor]);

  const loadEvents = () => api.get("/events", { params: { dal: fmtKey(range.start), al: fmtKey(range.end) } }).then((r) => setEvents(r.data));
  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [range.start, range.end]);
  useEffect(() => {
    if (isOwner) {
      api.get("/services").then((r) => setServices(r.data));
      api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    }
  }, [isOwner]);

  const filteredEvents = useMemo(() => {
    const q = courseQuery.trim().toLowerCase();
    return events.filter((e) => {
      const matchQ = !q || (e.servizio_nome || "").toLowerCase().includes(q);
      const matchMod = modFilter === "all" || (e.modalita || "aula") === modFilter;
      return matchQ && matchMod;
    });
  }, [events, courseQuery, modFilter]);

  const byDay = useMemo(() => {
    const m = {};
    filteredEvents.forEach((e) => {
      const gs = e.giornate?.length ? e.giornate : [{ data: e.data, ora_inizio: e.ora_inizio, ora_fine: e.ora_fine, ore: e.ore_totali }];
      gs.forEach((g) => { if (g.data) (m[g.data] = m[g.data] || []).push({ ...e, _g: g }); });
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a._g.ora_inizio || "").localeCompare(b._g.ora_inizio || "")));
    return m;
  }, [filteredEvents]);

  const programma = useMemo(() => {
    const todayK = fmtKey(new Date());
    const withDate = filteredEvents.map((e) => {
      const gs = e.giornate?.length ? e.giornate : [{ data: e.data }];
      const dates = gs.map((g) => g.data).filter(Boolean).sort();
      const next = dates.find((d) => d >= todayK) || dates[0] || "";
      return { ...e, _next: next };
    }).filter((e) => e._next);
    return withDate.sort((a, b) => a._next.localeCompare(b._next)).slice(0, 24);
  }, [filteredEvents]);

  const listGroups = useMemo(() => {
    const todayK = fmtKey(new Date());
    const rows = [];
    filteredEvents.forEach((e) => {
      const gs = e.giornate?.length ? e.giornate : [{ data: e.data, ora_inizio: e.ora_inizio, ora_fine: e.ora_fine }];
      gs.forEach((g) => { if (g.data && g.data >= todayK) rows.push({ ...e, _g: g }); });
    });
    rows.sort((a, b) => (a._g.data + (a._g.ora_inizio || "")).localeCompare(b._g.data + (b._g.ora_inizio || "")));
    const groups = []; const idx = {};
    rows.forEach((r) => {
      if (!(r._g.data in idx)) { idx[r._g.data] = groups.length; groups.push({ data: r._g.data, items: [] }); }
      groups[idx[r._g.data]].items.push(r);
    });
    return groups;
  }, [filteredEvents]);

  const move = (dir) => {
    if (view === "month") setCursor((c) => addMonths(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addDays(c, dir));
  };

  const periodLabel = view === "month"
    ? format(cursor, "LLLL yyyy", { locale: it })
    : view === "week"
      ? `${format(range.start, "d MMM", { locale: it })} – ${format(range.end, "d MMM yyyy", { locale: it })}`
      : format(cursor, "EEEE d MMMM yyyy", { locale: it });

  const openNew = (day) => setDialog({ mode: "new", form: { ...emptyEvent(), giornate: [{ data: day ? fmtKey(day) : fmtKey(cursor), ora_inizio: "09:00", ora_fine: "13:00", ore: "" }] } });
  const openEvent = async (e) => {
    let enrollments = [];
    try { enrollments = (await api.get(`/events/${e.id}/iscrizioni`)).data; } catch { /* noop */ }
    if (isOwner) {
      const giornate = (e.giornate?.length ? e.giornate : [{ data: e.data, ora_inizio: e.ora_inizio, ora_fine: e.ora_fine, ore: e.ore_totali }]).map((g) => ({ data: g.data, ora_inizio: g.ora_inizio || "", ora_fine: g.ora_fine || "", ore: g.ore ?? "" }));
      setDialog({ mode: "edit", id: e.id, enrollments, form: { servizio_id: e.servizio_id || "", titolo: e.servizio_id ? "" : e.servizio_nome, ore_totali: e.ore_totali ?? "", posti: e.posti ?? "", giornate, modalita: e.modalita, luogo: e.luogo || "", link: e.link || "", note: e.note || "" } });
    } else {
      const mine = enrollments[0];
      setDialog({ mode: "view", event: e, iscr: { id: mine?.id || null, stato: mine?.stato || null, azienda_nome: mine?.azienda_nome || "", corsisti: mine?.corsisti?.length ? mine.corsisti.map((c) => ({ ...EMPTY_CORSISTA, ...c })) : [{ ...EMPTY_CORSISTA }], note: mine?.note || "" } });
    }
  };
  const setGiornata = (i, k, v) => setDialog((d) => ({ ...d, form: { ...d.form, giornate: d.form.giornate.map((g, idx) => (idx === i ? { ...g, [k]: v } : g)) } }));
  const addGiornata = () => setDialog((d) => ({ ...d, form: { ...d.form, giornate: [...d.form.giornate, { data: fmtKey(cursor), ora_inizio: "09:00", ora_fine: "13:00", ore: "" }] } }));
  const removeGiornata = (i) => setDialog((d) => ({ ...d, form: { ...d.form, giornate: d.form.giornate.filter((_, idx) => idx !== i) } }));
  const loadPreview = async (mese) => {
    try {
      const r = await api.get("/events/calendar-preview", { params: mese ? { mese } : {} });
      setSendDlg((d) => (d ? { ...d, previewHtml: r.data.html, eventi: r.data.eventi } : d));
    } catch (e) { /* noop */ }
  };
  const openSendDialog = async () => {
    setSendDlg({ ids: partners.filter((p) => p.email).map((p) => p.id), previewHtml: null, eventi: null, mese: "all" });
    loadPreview(null);
  };
  const changeMese = (v) => {
    setSendDlg((d) => ({ ...d, mese: v, previewHtml: null, eventi: null }));
    loadPreview(v === "all" ? null : v);
  };
  const toggleSend = (id) => setSendDlg((d) => ({ ...d, ids: d.ids.includes(id) ? d.ids.filter((x) => x !== id) : [...d.ids, id] }));
  const sendCalendar = async () => {
    if (!sendDlg.ids.length) return toast.error("Seleziona almeno un partner");
    try {
      const r = await api.post("/events/send-calendar", { partner_ids: sendDlg.ids, mese: sendDlg.mese === "all" ? null : sendDlg.mese });
      if (r.data.inviate === 0) toast.warning(`Nessuna email inviata (${r.data.destinatari} partner selezionati). Con il mittente di prova Resend si invia solo alla tua email: verifica un dominio per inviare a tutti.`);
      else toast.success(`Calendario inviato a ${r.data.inviate} partner su ${r.data.destinatari} selezionati.`);
      setSendDlg(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const meseOptions = useMemo(() => {
    const opts = [];
    let d = startOfMonth(new Date());
    for (let i = 0; i < 12; i++) { opts.push({ value: format(d, "yyyy-MM"), label: format(d, "LLLL yyyy", { locale: it }) }); d = addMonths(d, 1); }
    return opts;
  }, []);

  // --- iscrizioni (partner) ---
  const setIscrC = (i, k, v) => setDialog((d) => ({ ...d, iscr: { ...d.iscr, corsisti: d.iscr.corsisti.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) } }));
  const addIscrC = () => setDialog((d) => ({ ...d, iscr: { ...d.iscr, corsisti: [...d.iscr.corsisti, { ...EMPTY_CORSISTA }] } }));
  const removeIscrC = (i) => setDialog((d) => ({ ...d, iscr: { ...d.iscr, corsisti: d.iscr.corsisti.filter((_, idx) => idx !== i) } }));
  const saveIscrizione = async () => {
    const azienda = (dialog.iscr.azienda_nome || "").trim();
    if (!azienda) return toast.error("Indica il nome dell'azienda");
    const corsisti = dialog.iscr.corsisti.filter((c) => c.nome || c.cognome || c.email);
    if (corsisti.length === 0) return toast.error("Aggiungi almeno un allievo (nome e cognome)");
    try {
      const r = await api.post(`/events/${dialog.event.id}/iscrizioni`, { azienda_nome: azienda, corsisti, note: dialog.iscr.note });
      const msg = r.data?.messaggio || "Abbiamo preso in carico la sua richiesta di pre-iscrizione.";
      toast.success(msg, { duration: 7000 });
      setDialog(null); loadEvents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteIscrizione = async () => {
    if (!dialog.iscr.id) return;
    if (!window.confirm("Annullare la pre-iscrizione a questo corso?")) return;
    await api.delete(`/events/${dialog.event.id}/iscrizioni/${dialog.iscr.id}`);
    toast.success("Pre-iscrizione annullata"); setDialog(null); loadEvents();
  };
  const setEnrollmentStato = async (iid, stato) => {
    try {
      await api.put(`/events/${dialog.id}/iscrizioni/${iid}/stato`, { stato });
      toast.success(stato === "confermata" ? "Pre-iscrizione confermata: il partner è stato avvisato" : "Pre-iscrizione rifiutata: il partner è stato avvisato");
      setDialog((d) => ({ ...d, enrollments: (d.enrollments || []).map((e) => e.id === iid ? { ...e, stato } : e) }));
      loadEvents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    const f = dialog.form;
    if (!f.servizio_id && !f.titolo.trim()) return toast.error("Seleziona un corso o inserisci un titolo");
    const giornate = f.giornate.filter((g) => g.data).map((g) => ({ ...g, ore: Number(g.ore) || 0 }));
    if (giornate.length === 0) return toast.error("Aggiungi almeno una giornata con data");
    const payload = { ...f, ore_totali: Number(f.ore_totali) || 0, posti: parseInt(f.posti) || 0, giornate };
    try {
      if (dialog.mode === "edit") await api.put(`/events/${dialog.id}`, payload);
      else await api.post("/events", payload);
      toast.success(dialog.mode === "edit" ? "Corso aggiornato" : "Corso aggiunto al calendario");
      setDialog(null); loadEvents();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare questo corso dal calendario?")) return;
    await api.delete(`/events/${dialog.id}`); toast.success("Corso eliminato"); setDialog(null); loadEvents();
  };

  const setF = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const monthDays = useMemo(() => {
    const days = []; let d = range.start;
    while (d <= range.end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [range]);

  const weekDays = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];

  const EventChip = ({ e }) => {
    const m = MODALITA[e.modalita] || MODALITA.aula;
    return (
      <button onClick={() => openEvent(e)} data-testid={`event-${e.id}`}
        className={`w-full text-left text-[11px] px-1.5 py-1 rounded border ${m.chip} truncate flex items-center gap-1 hover:opacity-90`}>
        {e.modalita === "videoconferenza" ? <Video className="h-3 w-3 shrink-0" /> : <MapPin className="h-3 w-3 shrink-0" />}
        {(e._g?.ora_inizio || e.ora_inizio) && <span className="font-semibold">{e._g?.ora_inizio || e.ora_inizio}</span>}
        <span className="truncate">{e.servizio_nome}</span>
        {isOwner && e.iscritti_count > 0 && <span className="ml-auto inline-flex items-center gap-0.5 shrink-0"><Users className="h-3 w-3" />{e.iscritti_count}</span>}
      </button>
    );
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Calendario Corsi</h1>
          <p className="text-sm text-slate-500 mt-1">{isOwner ? "Programma i corsi in aula o videoconferenza da condividere con i partner." : "I corsi in programmazione. Naviga tra i mesi per vedere le date."}</p>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Button onClick={openSendDialog} variant="outline" data-testid="send-calendar-btn"><Send className="h-4 w-4 mr-1.5" /> Invia calendario ai partner</Button>
            <Button onClick={() => openNew()} data-testid="add-event-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white"><Plus className="h-4 w-4 mr-1.5" /> Nuovo corso</Button>
          </div>
        )}
      </div>

      <Card className="p-4 border-0 shadow-sm overflow-hidden relative bg-gradient-to-br from-dusty-600 via-dusty-700 to-indigo-800 text-white" data-testid="cal-search-card">
        <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute right-24 -bottom-12 h-32 w-32 rounded-full bg-violet-300/20 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-white/90 text-sm font-semibold mb-3">
            <Sparkles className="h-4 w-4" /> Trova un corso in programma
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              <input value={courseQuery} onChange={(e) => setCourseQuery(e.target.value)} data-testid="cal-course-search"
                placeholder="Cerca per nome corso…"
                className="w-full h-10 pl-9 pr-9 rounded-xl bg-white/15 text-white placeholder-white/60 border border-white/25 focus:outline-none focus:ring-2 focus:ring-white/50" />
              {courseQuery && (
                <button onClick={() => setCourseQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white" data-testid="cal-course-clear"><X className="h-4 w-4" /></button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {[["all", "Tutti"], ["aula", "Aula"], ["videoconferenza", "Videoconf."]].map(([v, l]) => (
                <button key={v} onClick={() => setModFilter(v)} data-testid={`cal-mod-${v}`}
                  className={`text-xs font-medium px-3 h-10 rounded-xl border transition-colors ${modFilter === v ? "bg-white text-dusty-700 border-white" : "bg-white/10 text-white border-white/25 hover:bg-white/20"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="cal-programma">
            {programma.length === 0 && <span className="text-xs text-white/70">Nessun corso in programma per i filtri scelti.</span>}
            {programma.map((e, i) => {
              const vc = (e.modalita || "aula") === "videoconferenza";
              return (
                <button key={e.id + i} onClick={() => openEvent(e)} data-testid={`cal-prog-${e.id}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-white/95 text-slate-700 hover:bg-white shadow-sm max-w-[240px]">
                  <span className={`h-2 w-2 rounded-full ${vc ? "bg-violet-500" : "bg-dusty-500"}`} />
                  <span className="font-semibold text-slate-500">{fmtDayShort(e._next)}</span>
                  <span className="truncate">{e.servizio_nome}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <Card className="p-4 border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Oggi</Button>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <span className="ml-2 font-display font-semibold text-slate-800 capitalize" data-testid="cal-period">{periodLabel}</span>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[["month", "Mese"], ["week", "Settimana"], ["day", "Giorno"], ["list", "Lista"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${view === v ? "bg-white text-dusty-700 shadow-sm font-medium" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${MODALITA.aula.dot}`} /> Aula</span>
          <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${MODALITA.videoconferenza.dot}`} /> Videoconferenza</span>
        </div>

        {view === "month" && (
          <div>
            <div className="grid grid-cols-7 gap-px text-center text-xs font-medium text-slate-400 mb-1">
              {weekDays.map((d) => <div key={d} className="py-1 capitalize">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
              {monthDays.map((d) => {
                const key = fmtKey(d);
                const dayEvents = byDay[key] || [];
                const inMonth = isSameMonth(d, cursor);
                const today = isSameDay(d, new Date());
                return (
                  <div key={key} data-testid={`cal-day-${key}`}
                    className={`min-h-[104px] bg-white p-1.5 ${inMonth ? "" : "bg-slate-50/70"} ${isOwner ? "cursor-pointer hover:bg-dusty-50/40" : ""}`}
                    onClick={isOwner ? () => openNew(d) : undefined}>
                    <div className={`text-xs mb-1 ${today ? "h-5 w-5 rounded-full bg-dusty-600 text-white flex items-center justify-center" : inMonth ? "text-slate-600" : "text-slate-300"}`}>{format(d, "d")}</div>
                    <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                      {dayEvents.slice(0, 3).map((e) => <EventChip key={e.id} e={e} />)}
                      {dayEvents.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} altri</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "week" && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
            {monthDays.map((d) => {
              const key = fmtKey(d);
              const dayEvents = byDay[key] || [];
              const today = isSameDay(d, new Date());
              return (
                <div key={key} data-testid={`cal-day-${key}`} className="border border-slate-200 rounded-lg p-2 min-h-[140px]">
                  <div className={`text-xs font-medium mb-2 flex items-center justify-between ${today ? "text-dusty-700" : "text-slate-500"}`}>
                    <span className="capitalize">{format(d, "EEE d", { locale: it })}</span>
                    {isOwner && <button onClick={() => openNew(d)} className="text-slate-300 hover:text-dusty-600"><Plus className="h-3.5 w-3.5" /></button>}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.map((e) => <EventChip key={e.id} e={e} />)}
                    {dayEvents.length === 0 && <div className="text-[11px] text-slate-300">—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === "day" && (
          <div className="space-y-2">
            {(byDay[fmtKey(cursor)] || []).map((e) => {
              const m = MODALITA[e.modalita] || MODALITA.aula;
              return (
                <button key={e.id} onClick={() => openEvent(e)} data-testid={`event-${e.id}`}
                  className={`w-full text-left rounded-lg border p-3 flex items-start gap-3 hover:shadow-sm transition-shadow ${m.chip}`}>
                  <div className="flex flex-col items-center text-xs font-semibold min-w-[70px]">
                    <Clock className="h-3.5 w-3.5 mb-1" />
                    {e._g?.ora_inizio || e.ora_inizio || "—"}{(e._g?.ora_fine || e.ora_fine) ? `–${e._g?.ora_fine || e.ora_fine}` : ""}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{e.servizio_nome}</div>
                    <div className="text-xs flex items-center gap-1 mt-0.5">
                      {e.modalita === "videoconferenza" ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                      {m.label}{e.luogo ? ` · ${e.luogo}` : ""}{e.link ? " · link disponibile" : ""}
                    </div>
                  </div>
                </button>
              );
            })}
            {(byDay[fmtKey(cursor)] || []).length === 0 && <div className="text-slate-400 py-12 text-center">Nessun corso in programma per questo giorno.</div>}
          </div>
        )}

        {view === "list" && (
          <div className="space-y-5" data-testid="cal-list-view">
            {listGroups.length === 0 && (
              <div className="text-slate-400 py-16 text-center flex flex-col items-center gap-2">
                <CalendarDays className="h-8 w-8 text-slate-300" />
                Nessun corso in programma{courseQuery ? ` per «${courseQuery}»` : ""}.
              </div>
            )}
            {listGroups.map((grp) => (
              <div key={grp.data} data-testid={`cal-list-group-${grp.data}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-9 w-9 rounded-xl bg-dusty-600 text-white flex flex-col items-center justify-center leading-none shrink-0">
                    <span className="text-sm font-bold">{fmtDayNum(grp.data)}</span>
                    <span className="text-[9px] uppercase">{fmtMonShort(grp.data)}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-700 capitalize">{fmtDayFull(grp.data)}</div>
                  <span className="text-xs text-slate-400">· {grp.items.length} corso{grp.items.length > 1 ? "i" : ""}</span>
                </div>
                <div className="space-y-2 pl-1">
                  {grp.items.map((e, i) => {
                    const m = MODALITA[e.modalita] || MODALITA.aula;
                    const posti = isOwner && e.posti > 0 ? `${(e.iscritti_count ?? 0)}/${e.posti}` : null;
                    return (
                      <button key={e.id + i} onClick={() => openEvent(e)} data-testid={`cal-list-event-${e.id}`}
                        className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 hover:shadow-md transition-shadow ${m.chip}`}>
                        <div className="flex flex-col items-center text-xs font-semibold min-w-[68px] border-r border-black/10 pr-3">
                          <Clock className="h-3.5 w-3.5 mb-1" />
                          {e._g?.ora_inizio || e.ora_inizio || "—"}{(e._g?.ora_fine || e.ora_fine) ? `–${e._g?.ora_fine || e.ora_fine}` : ""}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{e.servizio_nome}</div>
                          <div className="text-xs flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {e.modalita === "videoconferenza" ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                            <span>{m.label}{e.luogo ? ` · ${e.luogo}` : ""}</span>
                            {posti && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/60"><Users className="h-3 w-3" /> {posti}</span>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 opacity-40 mt-1 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white" data-testid="event-dialog">
          {dialog?.mode === "view" ? (
            <>
              <DialogHeader><DialogTitle className="font-display">{dialog.event.servizio_nome}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">{dialog.event.modalita === "videoconferenza" ? <Video className="h-4 w-4 text-violet-500" /> : <MapPin className="h-4 w-4 text-dusty-500" />}{(MODALITA[dialog.event.modalita] || MODALITA.aula).label}{dialog.event.ore_totali ? ` · ${dialog.event.ore_totali} ore totali` : ""}</div>
                {dialog.event.posti > 0 && (
                  <div className="flex items-center gap-2" data-testid="event-posti-residui">
                    <Users className="h-4 w-4 text-dusty-500" />
                    <span>Posti edizione: <b>{dialog.event.posti}</b> · residui <b className={dialog.event.posti_residui > 0 ? "text-emerald-600" : "text-rose-600"}>{dialog.event.posti_residui}</b></span>
                  </div>
                )}
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {(dialog.event.giornate?.length ? dialog.event.giornate : [{ data: dialog.event.data, ora_inizio: dialog.event.ora_inizio, ora_fine: dialog.event.ora_fine }]).map((g, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                      <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="capitalize">{g.data ? format(parseISO(g.data), "EEE d MMM yyyy", { locale: it }) : "—"}</span>
                      {g.ora_inizio && <span className="text-slate-500">· {g.ora_inizio}{g.ora_fine ? `–${g.ora_fine}` : ""}</span>}
                      {g.ore ? <span className="ml-auto text-xs text-slate-400">{g.ore}h</span> : null}
                    </div>
                  ))}
                </div>
                {dialog.event.luogo && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{dialog.event.luogo}</div>}
                {dialog.event.link && <div className="flex items-center gap-2"><Video className="h-4 w-4 text-slate-400" /><a href={dialog.event.link} target="_blank" rel="noreferrer" className="text-dusty-700 hover:underline break-all">{dialog.event.link}</a></div>}
                {dialog.event.note && <div className="text-slate-500 pt-1">{dialog.event.note}</div>}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                {dialog.iscr.id && dialog.iscr.stato && (
                  <div className="mb-3 flex items-center gap-2 text-sm" data-testid="mia-preiscrizione-stato">
                    <span className="text-slate-500">Stato della tua pre-iscrizione:</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${dialog.iscr.stato === "confermata" ? "bg-emerald-100 text-emerald-700" : dialog.iscr.stato === "rifiutata" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {dialog.iscr.stato === "confermata" ? "Confermata · posto disponibile" : dialog.iscr.stato === "rifiutata" ? "Non accettata · posti esauriti" : "In verifica posti"}
                    </span>
                  </div>
                )}
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-3 text-xs text-amber-800" data-testid="pre-iscrizione-info">
                  <b>Questa è solo una pre-iscrizione.</b> L'amministrazione verificherà prima la disponibilità di posti nell'edizione in partenza e vi ricontatterà. Per iscrivere formalmente l'allievo utilizzate l'area <b>Catalogo e Ordini</b>.
                  <button type="button" onClick={() => { setDialog(null); navigate("/catalogo"); }} className="mt-2 inline-flex items-center gap-1 text-dusty-700 font-semibold hover:underline" data-testid="pre-iscrizione-goto-catalogo">
                    Vai a Catalogo e Ordini →
                  </button>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5"><Users className="h-4 w-4 text-dusty-600" /> Pre-iscrizione allievi</h4>
                  <button type="button" onClick={addIscrC} className="text-xs text-dusty-700 hover:underline inline-flex items-center gap-1" data-testid="iscr-add-corsista"><UserPlus className="h-3.5 w-3.5" /> Aggiungi</button>
                </div>
                <div className="mb-2">
                  <Label className="text-xs text-slate-500">Nome Azienda *</Label>
                  <Input value={dialog.iscr.azienda_nome} onChange={(e) => setDialog((d) => ({ ...d, iscr: { ...d.iscr, azienda_nome: e.target.value } }))} placeholder="Ragione sociale dell'azienda" className="h-9 text-sm mt-1" data-testid="iscr-azienda" />
                </div>
                <div className="space-y-2 max-h-[34vh] overflow-y-auto pr-1">
                  {dialog.iscr.corsisti.map((c, i) => (
                    <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2 relative" data-testid={`iscr-corsista-${i}`}>
                      {dialog.iscr.corsisti.length > 1 && (
                        <button type="button" onClick={() => removeIscrC(i)} className="absolute top-1.5 right-1.5 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                      )}
                      <div className="grid grid-cols-2 gap-2 pr-5">
                        <Input value={c.nome} onChange={(e) => setIscrC(i, "nome", e.target.value)} placeholder="Nome" className="h-8 text-sm" data-testid={`iscr-nome-${i}`} />
                        <Input value={c.cognome} onChange={(e) => setIscrC(i, "cognome", e.target.value)} placeholder="Cognome" className="h-8 text-sm" data-testid={`iscr-cognome-${i}`} />
                        <Input value={c.cellulare} onChange={(e) => setIscrC(i, "cellulare", e.target.value)} placeholder="Cellulare" className="h-8 text-sm" data-testid={`iscr-cellulare-${i}`} />
                        <Input value={c.email} onChange={(e) => setIscrC(i, "email", e.target.value)} placeholder="Email" className="h-8 text-sm" data-testid={`iscr-email-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
                <Textarea value={dialog.iscr.note} onChange={(e) => setDialog((d) => ({ ...d, iscr: { ...d.iscr, note: e.target.value } }))} placeholder="Note (opzionale)" className="mt-2" />
              </div>
              <DialogFooter className="flex items-center justify-between sm:justify-between">
                {dialog.iscr.id
                  ? <Button variant="ghost" onClick={deleteIscrizione} className="text-rose-600 hover:text-rose-700" data-testid="iscr-delete"><Trash2 className="h-4 w-4 mr-1" /> Annulla pre-iscrizione</Button>
                  : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDialog(null)}>Chiudi</Button>
                  <Button onClick={saveIscrizione} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="iscr-save">{dialog.iscr.id ? "Aggiorna pre-iscrizione" : "Invia pre-iscrizione"}</Button>
                </div>
              </DialogFooter>
            </>
          ) : dialog ? (
            <>
              <DialogHeader><DialogTitle className="font-display">{dialog.mode === "edit" ? "Modifica corso" : "Nuovo corso in calendario"}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-1 max-h-[68vh] overflow-y-auto pr-1">
                <div>
                  <Label>Corso dal catalogo</Label>
                  <Select value={dialog.form.servizio_id || "none"} onValueChange={(v) => setF("servizio_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1.5" data-testid="event-servizio"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Titolo libero —</SelectItem>
                      {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!dialog.form.servizio_id && (
                  <div><Label>Titolo</Label><Input value={dialog.form.titolo} onChange={(e) => setF("titolo", e.target.value)} data-testid="event-titolo" className="mt-1.5" placeholder="Titolo del corso" /></div>
                )}
                <div>
                  <Label>Ore totali del corso</Label>
                  <Input type="number" min="0" value={dialog.form.ore_totali} onChange={(e) => setF("ore_totali", e.target.value)} data-testid="event-ore-totali" className="mt-1.5" placeholder="es. 12" />
                </div>
                <div>
                  <Label>Posti disponibili (capienza edizione)</Label>
                  <Input type="number" min="0" value={dialog.form.posti} onChange={(e) => setF("posti", e.target.value)} data-testid="event-posti" className="mt-1.5" placeholder="0 = illimitati" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Giornate (data, fascia oraria, ore)</Label>
                    <button type="button" onClick={addGiornata} className="text-xs text-dusty-700 hover:underline inline-flex items-center gap-1" data-testid="event-add-giornata"><Plus className="h-3.5 w-3.5" /> Aggiungi giornata</button>
                  </div>
                  <div className="space-y-2">
                    {dialog.form.giornate.map((g, i) => (
                      <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_0.7fr_auto] gap-2 items-end" data-testid={`giornata-${i}`}>
                        <div><Input type="date" value={g.data} onChange={(e) => setGiornata(i, "data", e.target.value)} className="h-9" data-testid={`giornata-data-${i}`} /></div>
                        <div><Input type="time" value={g.ora_inizio} onChange={(e) => setGiornata(i, "ora_inizio", e.target.value)} className="h-9" data-testid={`giornata-inizio-${i}`} /></div>
                        <div><Input type="time" value={g.ora_fine} onChange={(e) => setGiornata(i, "ora_fine", e.target.value)} className="h-9" /></div>
                        <div><Input type="number" min="0" placeholder="ore" value={g.ore} onChange={(e) => setGiornata(i, "ore", e.target.value)} className="h-9" data-testid={`giornata-ore-${i}`} /></div>
                        <button type="button" onClick={() => removeGiornata(i)} disabled={dialog.form.giornate.length === 1} className="h-9 w-9 flex items-center justify-center text-slate-300 hover:text-rose-500 disabled:opacity-30"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-500">
                    Ore pianificate: <span className="font-semibold">{dialog.form.giornate.reduce((s, g) => s + (Number(g.ore) || 0), 0)}h</span>
                    {dialog.form.ore_totali ? ` su ${dialog.form.ore_totali}h totali` : ""}
                  </div>
                </div>
                <div>
                  <Label>Modalità</Label>
                  <Select value={dialog.form.modalita} onValueChange={(v) => setF("modalita", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="event-modalita"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aula">Aula</SelectItem>
                      <SelectItem value="videoconferenza">Videoconferenza</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {dialog.form.modalita === "aula"
                  ? <div><Label>Luogo / Aula</Label><Input value={dialog.form.luogo} onChange={(e) => setF("luogo", e.target.value)} data-testid="event-luogo" className="mt-1.5" placeholder="Indirizzo o sede" /></div>
                  : <div><Label>Link videoconferenza</Label><Input value={dialog.form.link} onChange={(e) => setF("link", e.target.value)} data-testid="event-link" className="mt-1.5" placeholder="https://…" /></div>}
                <div><Label>Note</Label><Textarea value={dialog.form.note} onChange={(e) => setF("note", e.target.value)} className="mt-1.5" /></div>

                {dialog.enrollments?.length > 0 && (
                  <div className="pt-3 border-t border-slate-100" data-testid="owner-enrollments">
                    <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-2"><Users className="h-4 w-4 text-dusty-600" /> Iscritti ({dialog.enrollments.reduce((n, e) => n + (e.corsisti?.length || 0), 0)})</h4>
                    <div className="space-y-2">
                      {dialog.enrollments.map((ins) => {
                        const st = ins.stato || "in_verifica";
                        const badge = st === "confermata" ? { c: "bg-emerald-100 text-emerald-700", t: "Confermata" }
                          : st === "rifiutata" ? { c: "bg-rose-100 text-rose-700", t: "Rifiutata" }
                          : { c: "bg-amber-100 text-amber-700", t: "Da verificare" };
                        return (
                        <div key={ins.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-sm" data-testid={`enrollment-${ins.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium text-slate-700">{ins.partner_nome}{ins.azienda_nome ? <span className="text-slate-400 font-normal"> · {ins.azienda_nome}</span> : null}</div>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.c}`} data-testid={`enrollment-stato-${ins.id}`}>{badge.t}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{ins.corsisti.map((c) => `${c.nome || ""} ${c.cognome || ""}`.trim()).filter(Boolean).join(", ")}</div>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant={st === "confermata" ? "default" : "outline"} onClick={() => setEnrollmentStato(ins.id, "confermata")} disabled={st === "confermata"} className={st === "confermata" ? "bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" : "h-7 text-xs text-emerald-700 border-emerald-200"} data-testid={`enrollment-confirm-${ins.id}`}>Conferma posto</Button>
                            <Button size="sm" variant={st === "rifiutata" ? "default" : "outline"} onClick={() => setEnrollmentStato(ins.id, "rifiutata")} disabled={st === "rifiutata"} className={st === "rifiutata" ? "bg-rose-600 hover:bg-rose-700 text-white h-7 text-xs" : "h-7 text-xs text-rose-700 border-rose-200"} data-testid={`enrollment-reject-${ins.id}`}>Rifiuta</Button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex items-center justify-between sm:justify-between">
                {dialog.mode === "edit"
                  ? <Button variant="ghost" onClick={del} data-testid="event-delete" className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>
                  : <span />}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button>
                  <Button onClick={save} data-testid="event-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
                </div>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDlg} onOpenChange={(v) => !v && setSendDlg(null)}>
        <DialogContent className="bg-white max-w-3xl w-[92vw]" data-testid="send-calendar-dialog">
          <DialogHeader><DialogTitle className="font-display">Invia calendario ai partner</DialogTitle></DialogHeader>
          {sendDlg && (
            <div className="space-y-4 py-1">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Periodo da inviare</Label>
                <Select value={sendDlg.mese} onValueChange={changeMese}>
                  <SelectTrigger className="mt-1.5" data-testid="send-cal-mese"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i corsi in programma</SelectItem>
                    {meseOptions.map((m) => <SelectItem key={m.value} value={m.value} className="capitalize">{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Anteprima email {sendDlg.eventi != null ? `· ${sendDlg.eventi} corsi` : ""}</span>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3" data-testid="calendar-preview">
                  {sendDlg.previewHtml == null
                    ? <div className="text-sm text-slate-400 py-6 text-center">Carico anteprima…</div>
                    : sendDlg.eventi === 0
                      ? <div className="text-sm text-amber-600 py-6 text-center">Nessun corso in programma da inviare.</div>
                      : <div dangerouslySetInnerHTML={{ __html: sendDlg.previewHtml }} />}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Scegli i partner a cui trasmettere.</p>
                <div className="flex gap-2 shrink-0">
                  <button className="text-xs text-dusty-700 hover:underline" data-testid="send-cal-all" onClick={() => setSendDlg((d) => ({ ...d, ids: partners.filter((p) => p.email).map((p) => p.id) }))}>Tutti</button>
                  <button className="text-xs text-slate-500 hover:underline" data-testid="send-cal-none" onClick={() => setSendDlg((d) => ({ ...d, ids: [] }))}>Nessuno</button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {partners.map((p) => {
                  const noEmail = !p.email;
                  const checked = sendDlg.ids.includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${noEmail ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"}`} data-testid={`send-cal-partner-${p.id}`}>
                      <input type="checkbox" className="h-4 w-4 accent-dusty-600" checked={checked} disabled={noEmail}
                        onChange={() => !noEmail && toggleSend(p.id)} data-testid={`send-cal-check-${p.id}`} />
                      <span className="flex-1">
                        <span className="font-medium text-slate-800">{p.nome}</span>
                        <span className="text-slate-400 ml-2 text-xs">{p.email || "nessuna email"}</span>
                      </span>
                    </label>
                  );
                })}
                {partners.length === 0 && <div className="px-3 py-6 text-center text-slate-400 text-sm">Nessun partner disponibile.</div>}
              </div>
              <div className="text-xs text-slate-500" data-testid="send-cal-count">{sendDlg.ids.length} partner selezionati</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDlg(null)}>Annulla</Button>
            <Button onClick={sendCalendar} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="send-cal-submit"><Send className="h-4 w-4 mr-1.5" /> Invia ai selezionati</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
